import { useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './features/auth/hooks/useAuth';
import { useAuthForm } from './features/auth/hooks/useAuthForm';
import { useIdleTimeout } from './features/auth/hooks/useIdleTimeout';
import { getPostAuthRedirectPath } from './features/auth/postAuthRouting';
import { ConfigProvider } from './features/flags/ConfigContext';
import {
  EntitlementsProvider,
  useEntitlements,
} from './features/entitlements/EntitlementsContext';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { LoginPage } from './features/auth/pages/LoginPage';
import { RenewPasswordPage } from './features/auth/pages/RenewPasswordPage';
import { BranchInitializationPage } from './features/branches/pages/BranchInitializationPage';
import { BranchSelectorPage } from './features/branches/pages/BranchSelectorPage';
import { BranchAccessWaitingPage } from './features/branches/pages/BranchAccessWaitingPage';
import { AppLayout } from './features/layout/components/AppLayout';
import { useLocale } from './features/locale/hooks/useLocale';
import { ToastProvider } from './features/shared/context/ToastContext';
import { ToastContainer } from './features/shared/components/ToastContainer';
import { applyTheme, parseThemeSetting } from './features/shared/utils/themeUtils';
import { DEFAULT_THEME_ID } from './styles/themes';
import styles from './App.module.css';

function AppContent() {
  const {
    authState,
    isAuthenticated,
    isChecking,
    requiresPasswordRenewal,
    login,
    renewPassword,
    logout,
    refreshSession,
    setBranchesInitialized,
    selectActiveBranch,
  } = useAuth();
  const { refetchEntitlements } = useEntitlements();
  const { locale, setLocale, t, isRtl } = useLocale();
  const authForm = useAuthForm({ login, logout, t });
  const branchesInitialized = authState.branchesInitialized;
  const needsBranchSelection = authState.needsBranchSelection;
  const activeBranch = authState.activeBranch;
  const accessibleBranchCount = authState.accessibleBranchCount;
  const needsBranchAccessWaiting =
    Boolean(branchesInitialized) && accessibleBranchCount === 0;

  const handleBranchInitializationComplete = useCallback(async () => {
    setBranchesInitialized(true);
    await refreshSession();
  }, [refreshSession, setBranchesInitialized]);

  const handleBranchSwitch = useCallback(
    async (branchId) => {
      await selectActiveBranch(branchId);
      await refetchEntitlements();
      await refreshSession();
    },
    [selectActiveBranch, refetchEntitlements, refreshSession],
  );

  useEffect(() => {
    if (isAuthenticated) {
      applyTheme(parseThemeSetting(authState.uiTheme));
    } else {
      applyTheme(DEFAULT_THEME_ID);
    }
  }, [isAuthenticated, authState.uiTheme]);

  useIdleTimeout({
    timeoutMinutes: authState.sessionTimeoutMinutes,
    enabled:
      isAuthenticated &&
      !isChecking &&
      !requiresPasswordRenewal &&
      Boolean(branchesInitialized) &&
      !needsBranchSelection &&
      !needsBranchAccessWaiting,
    onTimeout: authForm.handleLogout,
  });

  const authenticatedRedirect = (
    <Navigate
      to={getPostAuthRedirectPath({
        requiresPasswordRenewal,
        branchesInitialized,
        needsBranchAccessWaiting,
        needsBranchSelection,
      })}
      replace
    />
  );

  const postAuthGateLoading = null;

  const appLayoutGate =
    !isAuthenticated ? (
      <Navigate to="/login" replace />
    ) : isChecking ? (
      postAuthGateLoading
    ) : requiresPasswordRenewal ? (
      <Navigate to="/renew-password" replace />
    ) : !branchesInitialized ? (
      <Navigate to="/branch-initialization" replace />
    ) : needsBranchAccessWaiting ? (
      <Navigate to="/branch-access-waiting" replace />
    ) : needsBranchSelection ? (
      <Navigate to="/branch-selection" replace />
    ) : (
      <AppLayout
        user={authState.user}
        token={authState.token}
        entrySettings={authState.entrySettings}
        pricingSettings={authState.pricingSettings}
        organization={authState.organization}
        activeBranch={activeBranch}
        accessibleBranchCount={accessibleBranchCount}
        onBranchSwitch={handleBranchSwitch}
        onLogout={authForm.handleLogout}
        onRefreshSession={refreshSession}
        t={t}
        locale={locale}
        onLocaleChange={setLocale}
      />
    );

  return (
    <ConfigProvider>
      <ToastProvider>
        <div className={styles.app} dir={isRtl ? 'rtl' : 'ltr'}>
          <BrowserRouter>
            <Routes>
              <Route
                path="/login"
                element={
                  isAuthenticated ? (
                    isChecking ? null : authenticatedRedirect
                  ) : (
                    <LoginPage
                      authForm={authForm}
                      isChecking={isChecking}
                      t={t}
                      locale={locale}
                      onLocaleChange={setLocale}
                    />
                  )
                }
              />
              <Route
                path="/renew-password"
                element={
                  !isAuthenticated ? (
                    <Navigate to="/login" replace />
                  ) : isChecking ? null : !requiresPasswordRenewal ? (
                    authenticatedRedirect
                  ) : (
                    <RenewPasswordPage
                      renewPassword={renewPassword}
                      onLogout={authForm.handleLogout}
                      isChecking={isChecking}
                      t={t}
                      locale={locale}
                      onLocaleChange={setLocale}
                    />
                  )
                }
              />
              <Route
                path="/branch-initialization"
                element={
                  !isAuthenticated ? (
                    <Navigate to="/login" replace />
                  ) : isChecking ? null : requiresPasswordRenewal ? (
                    <Navigate to="/renew-password" replace />
                  ) : (
                    <BranchInitializationPage
                      user={authState.user}
                      token={authState.token}
                      t={t}
                      locale={locale}
                      onLocaleChange={setLocale}
                      onComplete={handleBranchInitializationComplete}
                      branchesInitialized={Boolean(branchesInitialized)}
                    />
                  )
                }
              />
              <Route
                path="/branch-access-waiting"
                element={
                  !isAuthenticated ? (
                    <Navigate to="/login" replace />
                  ) : isChecking ? null : requiresPasswordRenewal ? (
                    <Navigate to="/renew-password" replace />
                  ) : !branchesInitialized ? (
                    <Navigate to="/branch-initialization" replace />
                  ) : !needsBranchAccessWaiting ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <BranchAccessWaitingPage
                      t={t}
                      locale={locale}
                      onLocaleChange={setLocale}
                      onLogout={authForm.handleLogout}
                      branchesInitialized={Boolean(branchesInitialized)}
                    />
                  )
                }
              />
              <Route
                path="/branch-selection"
                element={
                  !isAuthenticated ? (
                    <Navigate to="/login" replace />
                  ) : isChecking ? null : requiresPasswordRenewal ? (
                    <Navigate to="/renew-password" replace />
                  ) : !branchesInitialized ? (
                    <Navigate to="/branch-initialization" replace />
                  ) : needsBranchAccessWaiting ? (
                    <Navigate to="/branch-access-waiting" replace />
                  ) : !needsBranchSelection ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <BranchSelectorPage
                      token={authState.token}
                      t={t}
                      locale={locale}
                      onLocaleChange={setLocale}
                      onBranchSwitch={handleBranchSwitch}
                      needsBranchSelection={needsBranchSelection}
                      branchesInitialized={Boolean(branchesInitialized)}
                    />
                  )
                }
              />
              <Route path="/*" element={appLayoutGate} />
            </Routes>
          </BrowserRouter>
          <ToastContainer t={t} />
        </div>
      </ToastProvider>
    </ConfigProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <EntitlementsProvider>
        <AppContent />
      </EntitlementsProvider>
    </AuthProvider>
  );
}

export default App;
